import { missingNodeProperties, recipeFor } from "./catalog";
import type { StructuredDataConfig } from "./config";
import type { CatalogBrand, CatalogUnit } from "./types";
import { containers } from "./vocabulary";

export interface BuildNodeParams {
  config: StructuredDataConfig;
  /** Valores do registro, por **nome** de campo. */
  values: Record<string, unknown>;
  /** Tipo de cada campo, por nome. Vem do `_metadata.fields` da resposta pública. */
  fieldTypes: Record<string, string>;
  resolveUnit?: (unitId: string) => CatalogUnit | null | undefined;
  resolveBrand?: (brandId: string) => CatalogBrand | null | undefined;
  /** Monta o `@id` da unidade. O catálogo não conhece o esquema de URN. */
  unitReferenceId?: (unit: CatalogUnit) => string | null;
}

/**
 * Um galho da árvore de containers. `values` são as propriedades folha deste
 * nível; `children` são os containers aninhados dentro dele.
 */
interface Branch {
  values: Record<string, unknown>;
  children: Map<string, Branch>;
}

function emptyBranch(): Branch {
  return { values: {}, children: new Map() };
}

/** Desce (criando) até o galho do caminho, sem a folha. */
function branchAt(root: Branch, segments: string[]): Branch {
  return segments.reduce((branch, segment) => {
    const child = branch.children.get(segment) ?? emptyBranch();
    branch.children.set(segment, child);

    return child;
  }, root);
}

/**
 * Transforma o galho no objeto final. O `@type` e as constantes de cada nível
 * saem do **prefixo acumulado** — `offers` vira `Offer`,
 * `offers.priceSpecification` vira `UnitPriceSpecification`. Constantes entram
 * antes das propriedades mapeadas, para que o dado do registro sempre vença.
 */
function materialize(branch: Branch, prefix: string[]): Record<string, unknown> {
  const container = prefix.length > 0 ? containers[prefix.join(".")] : undefined;

  const node: Record<string, unknown> = container
    ? { "@type": container.type, ...(container.constants ?? {}) }
    : {};

  Object.assign(node, branch.values);

  branch.children.forEach((child, name) => {
    node[name] = materialize(child, [...prefix, name]);
  });

  return node;
}

/**
 * Dois campos podem alimentar a mesma propriedade — imagem de capa e galeria
 * ambas viram `image`. Quando os dois lados são lista, a propriedade recebe a
 * união; fora disso vale a primeira definição, a mesma convenção que o
 * `mergeById` usa ao consolidar nós.
 */
function combine(existing: unknown, next: unknown): unknown {
  if (existing === undefined) return next;

  if (Array.isArray(existing) && Array.isArray(next)) {
    return [...existing, ...next.filter((item) => !existing.includes(item))];
  }

  return existing;
}

/**
 * Transforma a configuração do tipo de conteúdo mais os valores de um registro
 * no nó schema.org correspondente. Devolve `null` quando não há entidade
 * configurada ou quando o registro não atinge o mínimo de propriedades.
 *
 * O nó sai **sem `@id`**: a identidade é do grafo, não do catálogo.
 */
export function buildNode({
  config,
  values,
  fieldTypes,
  resolveUnit,
  resolveBrand,
  unitReferenceId,
}: BuildNodeParams): Record<string, unknown> | null {
  if (config.types.length === 0) return null;

  const root = emptyBranch();
  const presentPaths: string[] = [];

  config.mappings.forEach(({ field, property, valueMap }) => {
    const recipe = recipeFor(property);
    if (!recipe) return;

    const emitted = recipe.emit({
      value: values[field],
      fieldType: fieldTypes[field] ?? "text",
      valueMap,
      resolveUnit,
      resolveBrand,
      unitReferenceId,
    });

    if (emitted === undefined) return;

    const segments = property.split(".");
    const leaf = segments.pop() as string;
    const branch = branchAt(root, segments);

    branch.values[leaf] = combine(branch.values[leaf], emitted);
    presentPaths.push(property);
  });

  const missing = missingNodeProperties({ types: config.types, presentPaths });
  if (missing.length > 0) return null;

  return {
    "@type": config.types.length === 1 ? config.types[0] : [...config.types],
    ...materialize(root, []),
  };
}
